import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatNativeDateModule, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { Dropdownfield, DropdownOption } from '../dropdownfield/dropdownfield';
import { Loader } from '../shared/loader/loader';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { Calendar, CUSTOM_DATE_FORMATS } from '../calendar/calendar';
import {
  ChangeLogsApiService,
  LogEntry,
  LookupItem
} from '../shared/services/change-logs-api.service';

type FieldErrors = {
  title?: string;
  summary?: string;
  change_date?: string;
  change_time?: string;
  commit_type_id?: string;
  commit_link?: string;
};

@Component({
  selector: 'app-change-logs-admin',
  imports: [
    CommonModule, FormsModule, Dropdownfield, Loader, Navbar, Footer, Calendar,
    MatDatepickerModule, MatInputModule, MatFormFieldModule, MatButtonModule, MatNativeDateModule,
    MatIconModule, MatCardModule, MatChipsModule, MatButtonToggleModule, MatTableModule,
    MatTooltipModule, MatDividerModule, MatSelectModule, MatMenuModule
  ],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }],
  templateUrl: './change-logs-admin.html',
  styleUrl: './change-logs-admin.css'
})
export class ChangeLogsAdmin implements OnInit {
  entries: LogEntry[] = [];
  isLoading = false;
  errorMessage = '';

  commitTypes: LookupItem[] = [];
  commitTypeOptions: DropdownOption[] = [];
  displayedColumns = ['date', 'title', 'environment', 'type', 'module', 'commit', 'author', 'actions'];

  filterCommitType = '';
  filterSearch = '';
  filterEnvironment = '';
  filterDateFrom: Date | null = null;
  filterDateTo: Date | null = null;
  activeChip = 'all';
  viewMode: 'table' | 'timeline' = 'table';

  page = 1;
  pageSize = 15;
  total = 0;
  filtersApplied = false;

  profileMenuOpen = false;

  modalOpen = false;
  modalMode: 'add' | 'edit' = 'add';
  saving = false;
  submitError = '';
  fieldErrors: FieldErrors = {};

  formId: number | null = null;
  formChangeDate = '';
  formChangeTime = '';
  formTitle = '';
  formSummary = '';
  formCommitTypeId = '';
  formCommitLink = '';

  deleteConfirmOpen = false;
  deleteTarget: LogEntry | null = null;
  deleting = false;

  detailModalOpen = false;
  detailEntry: LogEntry | null = null;

  constructor(
    private api: ChangeLogsApiService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    dateAdapter: DateAdapter<Date>
  ) {
    const originalFormat = dateAdapter.format.bind(dateAdapter);
    dateAdapter.format = (date: Date, displayFormat: any) => {
      if (date) {
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
      }
      return originalFormat(date, displayFormat);
    };
  }

  ngOnInit(): void {
    this.loadLookups();
  }

  get admin() {
    return this.api.currentAdmin;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get displayedEntries(): LogEntry[] {
    const env = (this.filterEnvironment || '').trim().toLowerCase();
    if (!env) return this.entries;
    return this.entries.filter(e => (e.environment?.name || '').toLowerCase() === env);
  }

  get productionCount(): number {
    return this.entries.filter(e => (e.environment?.name || '').toLowerCase() === 'production').length;
  }

  get featureCount(): number {
    return this.entries.filter(e => {
      const n = (e.commit_type?.name || '').toLowerCase();
      return n === 'feature' || n.includes('feature');
    }).length;
  }

  get productionShareLabel(): string {
    if (!this.entries.length) return 'No rows on this page';
    const pct = ((this.productionCount / this.entries.length) * 100).toFixed(1);
    return `${pct}% of this page`;
  }

  get lastDeploymentAge(): string {
    const newest = this.entries[0];
    if (!newest?.change_datetime) return '—';
    const ms = Date.now() - new Date(newest.change_datetime).getTime();
    if (isNaN(ms) || ms < 0) return '—';
    const hours = Math.floor(ms / 3600000);
    if (hours < 1) return `${Math.max(1, Math.floor(ms / 60000))}m`;
    if (hours < 48) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  get lastDeploymentTitle(): string {
    return this.entries[0]?.title || 'No deployments yet';
  }

  get rangeStart(): number {
    if (!this.total) return 0;
    return (this.page - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  get pageButtons(): Array<number | string> {
    const total = this.totalPages;
    const current = this.page;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: Array<number | string> = [1];
    if (current > 3) pages.push('...');
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }

  logout(): void {
    this.profileMenuOpen = false;
    this.api.clearSession();
    this.router.navigate(['/change-logs/login']);
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  closeProfileMenu(): void {
    this.profileMenuOpen = false;
  }

  switchView(mode: 'table' | 'timeline'): void {
    this.viewMode = mode;
  }

  private handleAuthError(err: any): boolean {
    if (err?.status === 401 || err?.status === 403) {
      this.logout();
      return true;
    }
    return false;
  }

  loadLookups(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.api.getCommitTypes().subscribe({
      next: (commitTypes) => {
        this.commitTypes = commitTypes;
        this.commitTypeOptions = commitTypes.map(c => ({ value: String(c.id), label: c.name }));
        this.cdr.detectChanges();
        this.loadEntries();
      },
      error: (err) => {
        this.isLoading = false;
        if (this.handleAuthError(err)) return;
        this.errorMessage = 'Failed to load filter options. Please refresh the page.';
        this.cdr.detectChanges();
      }
    });
  }

  loadEntries(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.api.listEntries(
      {
        commit_type_id: this.filterCommitType ? Number(this.filterCommitType) : null,
        search: this.filterSearch.trim() || null,
        date_from: this.filterDateFrom ? this.startOfDayIso(this.filterDateFrom) : null,
        date_to: this.filterDateTo ? this.endOfDayIso(this.filterDateTo) : null
      },
      this.page,
      this.pageSize
    ).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.entries = res.items;
        this.total = res.total;
        this.page = res.page;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        if (this.handleAuthError(err)) return;
        this.errorMessage = 'Failed to load change log entries.';
        this.cdr.detectChanges();
      }
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.filtersApplied = !!(
      this.filterCommitType ||
      this.filterSearch.trim() ||
      this.filterDateFrom ||
      this.filterDateTo ||
      this.filterEnvironment
    );
    if (this.filterCommitType) this.activeChip = `type:${this.filterCommitType}`;
    else if (this.filterEnvironment === 'production') this.activeChip = 'production';
    else if (!this.filtersApplied) this.activeChip = 'all';
    this.loadEntries();
  }

  resetFilters(): void {
    this.filterCommitType = '';
    this.filterSearch = '';
    this.filterEnvironment = '';
    this.filterDateFrom = null;
    this.filterDateTo = null;
    this.filtersApplied = false;
    this.activeChip = 'all';
    this.page = 1;
    this.loadEntries();
  }

  quickFilter(kind: string, type?: LookupItem): void {
    if (kind === 'all') {
      this.filterCommitType = '';
      this.filterEnvironment = '';
      this.activeChip = 'all';
    } else if (kind === 'production') {
      this.filterCommitType = '';
      this.filterEnvironment = 'production';
      this.activeChip = 'production';
    } else if (kind === 'type' && type) {
      this.filterEnvironment = '';
      this.filterCommitType = String(type.id);
      this.activeChip = `type:${type.id}`;
    }
    this.page = 1;
    this.filtersApplied = !!(this.filterCommitType || this.filterEnvironment || this.filterSearch.trim() || this.filterDateFrom || this.filterDateTo);
    this.loadEntries();
  }

  goToPage(n: number): void {
    if (n < 1 || n > this.totalPages || n === this.page) return;
    this.page = n;
    this.loadEntries();
  }

  onPageButtonClick(p: number | string): void {
    if (p === '...') return;
    this.goToPage(Number(p));
  }

  openAddModal(): void {
    this.modalMode = 'add';
    this.formId = null;
    const now = new Date();
    this.formChangeDate = this.dateToMMDDYYYY(now);
    this.formChangeTime = this.dateToHHMM(now);
    this.formTitle = '';
    this.formSummary = '';
    this.formCommitTypeId = '';
    this.formCommitLink = '';
    this.fieldErrors = {};
    this.submitError = '';
    this.modalOpen = true;
  }

  openEditModal(entry: LogEntry): void {
    this.modalMode = 'edit';
    this.formId = entry.id;
    const d = new Date(entry.change_datetime);
    this.formChangeDate = this.dateToMMDDYYYY(d);
    this.formChangeTime = this.dateToHHMM(d);
    this.formTitle = entry.title;
    this.formSummary = entry.summary;
    this.formCommitTypeId = String(entry.commit_type.id);
    this.formCommitLink = entry.commit_link || '';
    this.fieldErrors = {};
    this.submitError = '';
    this.modalOpen = true;
    this.cdr.detectChanges();
  }

  openDetailDrawer(entry: LogEntry): void {
    this.detailEntry = entry;
    this.detailModalOpen = true;
    this.cdr.detectChanges();
  }

  closeDetailDrawer(): void {
    this.detailModalOpen = false;
    this.detailEntry = null;
  }

  editFromDetail(entry: LogEntry): void {
    this.closeDetailDrawer();
    this.openEditModal(entry);
  }

  closeModal(): void {
    if (this.saving) return;
    this.modalOpen = false;
  }

  saveEntry(): void {
    this.fieldErrors = {};
    this.submitError = '';

    const title = this.formTitle.trim();
    const summary = this.formSummary.trim();
    const link = this.formCommitLink.trim();

    if (!this.formChangeDate) this.fieldErrors.change_date = 'Date is required.';
    if (!this.formChangeTime) this.fieldErrors.change_time = 'Time is required.';
    if (!title) this.fieldErrors.title = 'Title is required.';
    if (!summary) this.fieldErrors.summary = 'Summary is required.';
    if (!this.formCommitTypeId) this.fieldErrors.commit_type_id = 'Commit type is required.';
    if (link && !/^https?:\/\/.+/i.test(link)) {
      this.fieldErrors.commit_link = 'Link must start with http:// or https://';
    }

    if (Object.keys(this.fieldErrors).length > 0) return;

    const changeDateTime = this.mmddyyyyAndTimeToDate(this.formChangeDate, this.formChangeTime);
    if (!changeDateTime) {
      this.fieldErrors.change_date = 'Enter a valid date and time.';
      return;
    }

    this.saving = true;
    const payload = {
      change_datetime: changeDateTime.toISOString(),
      title,
      summary,
      commit_type_id: Number(this.formCommitTypeId),
      commit_link: link || null
    };

    const request = this.modalMode === 'add'
      ? this.api.createEntry(payload)
      : this.api.updateEntry(this.formId!, payload);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.modalOpen = false;
        this.cdr.detectChanges();
        this.loadEntries();
      },
      error: (err) => {
        this.saving = false;
        if (this.handleAuthError(err)) return;
        this.submitError = err?.error?.detail || 'Failed to save change log entry.';
        this.cdr.detectChanges();
      }
    });
  }

  openDeleteConfirm(entry: LogEntry): void {
    this.deleteTarget = entry;
    this.deleteConfirmOpen = true;
    this.cdr.detectChanges();
  }

  closeDeleteConfirm(): void {
    if (this.deleting) return;
    this.deleteConfirmOpen = false;
    this.deleteTarget = null;
  }

  confirmDelete(): void {
    if (!this.deleteTarget) return;
    this.deleting = true;
    this.api.deleteEntry(this.deleteTarget.id).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteConfirmOpen = false;
        this.deleteTarget = null;
        this.cdr.detectChanges();
        this.loadEntries();
      },
      error: (err) => {
        this.deleting = false;
        if (this.handleAuthError(err)) return;
        this.deleteConfirmOpen = false;
        this.errorMessage = 'Failed to delete change log entry.';
        this.cdr.detectChanges();
      }
    });
  }

  envBadgeClass(name: string): string {
    const n = (name || '').toLowerCase();
    if (n === 'production') return 'badge-prod';
    if (n === 'staging') return 'badge-stage';
    if (n === 'develop' || n === 'development' || n === 'dev') return 'badge-dev';
    return 'badge-slate';
  }

  commitTypeBadgeClass(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.includes('feature')) return 'badge-feature';
    if (n.includes('hotfix')) return 'badge-hotfix';
    if (n.includes('bug') || n === 'fix') return 'badge-fix';
    if (n.includes('chore')) return 'badge-chore';
    return 'badge-slate';
  }

  formatDay(iso: string): string {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }

  formatTime(iso: string): string {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  commitLabel(link: string): string {
    try {
      const path = new URL(link).pathname.replace(/\/+$/, '');
      const parts = path.split('/').filter(Boolean);
      return parts[parts.length - 1] || 'View';
    } catch {
      return 'View';
    }
  }

  authorInitial(entry: LogEntry): string {
    return (entry.created_by.name || entry.created_by.email || '?').charAt(0).toUpperCase();
  }

  authorPicture(entry: LogEntry | null | undefined): string | null {
    if (!entry?.created_by) return null;
    const fromApi = (entry.created_by.picture || '').trim();
    if (fromApi) return fromApi;
    const admin = this.admin;
    if (admin?.picture && admin.email && entry.created_by.email
      && admin.email.toLowerCase() === entry.created_by.email.toLowerCase()) {
      return admin.picture;
    }
    return null;
  }

  private startOfDayIso(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
  }

  private endOfDayIso(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
  }

  private dateToMMDDYYYY(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${d.getFullYear()}`;
  }

  private dateToHHMM(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private mmddyyyyAndTimeToDate(mmddyyyy: string, hhmm: string): Date | null {
    const dateMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec((mmddyyyy || '').trim());
    const timeMatch = /^(\d{2}):(\d{2})$/.exec((hhmm || '').trim());
    if (!dateMatch || !timeMatch) return null;
    const [, month, day, year] = dateMatch;
    const [, hours, minutes] = timeMatch;
    const d = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  trackById(_: number, e: LogEntry): number {
    return e.id;
  }
}
